/** One bookable time (or the customer's own appointment) in a day column. */
import { FC } from "react"
import { Pressable, PressableStateCallbackType, TextStyle, View, ViewStyle } from "react-native"

import { Text } from "@/components/Text"

import { webTransition } from "./motion"
import { font, tabular, tokens } from "./tokens"

export interface SlotButtonProps {
  time: string
  label: string
  recommended?: boolean
  /** The customer's current appointment: disabled, struck through, "Ihr Termin". */
  mine?: boolean
  onPress: () => void
  onFocus?: () => void
  onBlur?: () => void
}

type PressState = PressableStateCallbackType & { hovered?: boolean; focused?: boolean }

export const SlotButton: FC<SlotButtonProps> = ({
  time,
  label,
  recommended = false,
  mine = false,
  onPress,
  onFocus,
  onBlur,
}) => (
  <Pressable
    accessibilityRole="button"
    accessibilityLabel={label}
    accessibilityState={{ disabled: mine }}
    disabled={mine}
    onPress={onPress}
    onFocus={onFocus}
    onBlur={onBlur}
    testID={mine ? `slot-mine-${time}` : `slot-${time}`}
    style={(state) => {
      const { pressed, hovered, focused } = state as PressState
      return [
        $slot,
        recommended && $recommended,
        hovered && !mine && !pressed && $hover,
        mine && $mine,
        focused && $focus,
        pressed && !mine && $pressed,
      ]
    }}
  >
    {(state) => (
      <>
        {(recommended || mine) && (
          <View pointerEvents="none" style={$pillWrap}>
            <Text style={[$pill, mine ? $pillMine : $pillRecommended]}>
              {mine ? "Ihr Termin" : "Empfohlen"}
            </Text>
          </View>
        )}
        <Text style={[$text, mine && $textMine, state.pressed && !mine && $textPressed]}>
          {time}
        </Text>
      </>
    )}
  </Pressable>
)

const $slot: ViewStyle = {
  minHeight: 48,
  width: "100%",
  flexShrink: 0,
  borderRadius: 12,
  backgroundColor: tokens.mist,
  alignItems: "center",
  justifyContent: "center",
  ...webTransition("box-shadow, background-color", 150),
}
const $hover: ViewStyle = { borderWidth: 1, borderColor: tokens.field }
const $recommended: ViewStyle = {
  backgroundColor: tokens.surface,
  borderWidth: 2,
  borderColor: tokens.okRing,
}
const $mine: ViewStyle = {
  backgroundColor: tokens.surface,
  borderWidth: 1,
  borderColor: tokens.line,
}
const $pressed: ViewStyle = { backgroundColor: tokens.ink, borderWidth: 0 }
const $focus = {
  outlineStyle: "solid",
  outlineWidth: 2,
  outlineColor: tokens.ink,
  outlineOffset: -2,
} as ViewStyle
const $text: TextStyle = {
  fontSize: 16,
  fontFamily: font.bold,
  color: tokens.ink,
  ...tabular,
}
const $textMine: TextStyle = { color: tokens.ink45, textDecorationLine: "line-through" }
const $textPressed: TextStyle = { color: tokens.surface }
const $pillWrap: ViewStyle = {
  position: "absolute",
  top: -10,
  left: 0,
  right: 0,
  alignItems: "center",
}
const $pill: TextStyle = {
  fontSize: 11,
  lineHeight: 11,
  fontFamily: font.bold,
  color: tokens.surface,
  paddingVertical: 3,
  paddingHorizontal: 6,
  borderRadius: 300,
  overflow: "hidden",
}
const $pillRecommended: TextStyle = { backgroundColor: tokens.okRing }
const $pillMine: TextStyle = { backgroundColor: tokens.ink }
